/**
 * CompleteEnhancedResumeBuilder.jsx
 *
 * Orchestrator only — no business logic, no step JSX.
 *
 * Architecture:
 *   useResumeForm   → all form state + mutations
 *   useResumeSteps  → step navigation
 *   stepRegistry    → maps step IDs → lazy-loaded components
 *   STEPS           → single source of truth (resumeConstants.js)
 *
 * Adding a new step:
 *   1. Create src/components/resume/steps/MyStep.jsx
 *   2. Register it in stepRegistry.jsx
 *   3. Add to STEPS in resumeConstants.js
 *   — No changes needed here.
 */

import React, { useState, useCallback, useEffect, useRef } from 'react';
import { ArrowLeft, Download, Eye, CheckCircle, X, ChevronRight, Menu, Sparkles, Trash2, AlertCircle } from 'lucide-react';
import { useResumeForm } from '../hooks/useResumeForm';
import { useResumeSteps } from '../hooks/useResumeSteps';
import { useResumeAI } from '../hooks/useResumeAI';
import { STEPS } from './resume/resumeConstants';
import { renderStep } from './resume/stepRegistry';
import { useKeyboardOpen } from '../hooks/useKeyboardOpen';
import { resumeTemplates } from '../utils/resumeExportSimple';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import PremiumModal from './PremiumModal';
import toast from 'react-hot-toast';
import {
  ATSScoreSidebar,
  ATSScoreHeaderPill,
  ATSScoreMobileDrawer,
} from './resume/ui/ATSScoreWidget';
import { JobTargetBanner } from './resume/ui/JobTargetBanner';
import { JobTargetFlow } from './resume/ui/JobTargetFlow';

const JT_DISMISSED_KEY = 'jt_modal_dismissed';

// ── Sidebar nav item ──────────────────────────────────────────────────────────
const SidebarItem = ({ step, index, isActive, isCompleted, onClick }) => {
  const Icon = step.icon;
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => onClick(index)}
      onKeyDown={e => (e.key === 'Enter' || e.key === ' ') && onClick(index)}
      className={`flex items-center gap-3 p-3 rounded-xl cursor-pointer transition-all min-h-[48px] ${
        isActive    ? 'bg-blue-600 text-white shadow-lg'
        : isCompleted ? 'bg-blue-50 text-blue-700 hover:bg-blue-100'
        : 'text-gray-600 hover:bg-gray-100'
      }`}
    >
      <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
        isActive ? 'bg-white/20' : isCompleted ? 'bg-blue-100' : 'bg-gray-200'
      }`}>
        <Icon className={`w-4 h-4 ${isActive ? 'text-white' : isCompleted ? 'text-blue-600' : 'text-gray-500'}`} />
      </div>
      <span className="font-medium text-sm flex-1 leading-tight">{step.title}</span>
      {isCompleted && <div className="w-2 h-2 bg-green-500 rounded-full flex-shrink-0" />}
    </div>
  );
};

// ── Review / Download step (kept inline — unique, not reusable) ───────────────
const ReviewStep = ({ atsResult, selectedTemplate, setSelectedTemplate, handlePreview, handlePrint, downloadResume, isDownloading }) => {
  const r     = atsResult;
  const total = r?.total ?? 0;
  const bd    = r?.breakdown;

  const gradeColor  = total >= 80 ? 'text-green-600' : total >= 60 ? 'text-yellow-600' : 'text-red-600';
  const barColor    = total >= 80 ? 'bg-green-500'   : total >= 60 ? 'bg-yellow-500'   : 'bg-red-500';
  const priorityBadge = p => ({ critical: 'bg-red-100 text-red-700', high: 'bg-orange-100 text-orange-700', medium: 'bg-yellow-100 text-yellow-700', low: 'bg-blue-100 text-blue-700' }[p] || 'bg-gray-100 text-gray-600');
  const catLabels   = { testingTypes: 'Testing Types', automation: 'Automation Frameworks', aiTools: 'AI & ML Tools', cicd: 'CI/CD & DevOps', cloud: 'Cloud Platforms', tools: 'QA Tools', programming: 'Programming Languages', methodology: 'Methodology & Process' };

  return (
    <div>
      <div className="text-center mb-8">
        <div className="w-16 h-16 bg-blue-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
          <Download className="w-8 h-8 text-white" />
        </div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">ATS Score & Download</h2>
        <p className="text-gray-500 text-sm max-w-md mx-auto">ATS-optimised, single-column, semantic HTML for maximum parser compatibility.</p>
      </div>

      {/* Score card */}
      <div className="bg-white border border-gray-200 rounded-2xl p-6 mb-6 shadow-sm">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h3 className="font-bold text-gray-900 text-lg">ATS Compatibility Score</h3>
            <p className="text-gray-500 text-xs mt-0.5">Keyword density · Section completeness · Metrics · Format</p>
          </div>
          <div className="text-right">
            <span className={`text-4xl font-black ${gradeColor}`}>{total}</span>
            <span className="text-gray-400 text-lg">/100</span>
            <div className={`text-sm font-bold ${gradeColor}`}>Grade {r?.grade}</div>
          </div>
        </div>
        <div className="w-full bg-gray-100 rounded-full h-3 mb-5">
          <div className={`h-3 rounded-full transition-all duration-700 ${barColor}`} style={{ width: `${total}%` }} />
        </div>

        {bd && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
            {[['Keywords', bd.keywords], ['Sections', bd.sections], ['Metrics', bd.metrics], ['Format', bd.format]].map(([label, dim]) => (
              <div key={label} className="bg-gray-50 rounded-xl p-3 text-center border border-gray-100">
                <div className="text-xs text-gray-500 mb-1">{label}</div>
                <div className="font-bold text-gray-900">{dim.score}<span className="text-gray-400 font-normal text-xs">/{dim.max}</span></div>
                <div className="w-full bg-gray-200 rounded-full h-1.5 mt-1.5">
                  <div className="h-1.5 rounded-full bg-blue-500" style={{ width: `${Math.round((dim.score / dim.max) * 100)}%` }} />
                </div>
              </div>
            ))}
          </div>
        )}

        {bd && (
          <div className="flex items-center gap-2 text-sm text-gray-600 mb-4 bg-gray-50 rounded-lg px-3 py-2">
            <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
            <span>
              <strong>{bd.metrics.bulletsWithMetrics}</strong> of <strong>{bd.metrics.totalBullets}</strong> bullets contain quantified metrics
              {bd.metrics.bulletsWithMetrics < 3 && <span className="text-orange-600 ml-1">— aim for 5+</span>}
            </span>
          </div>
        )}

        {r?.suggestions?.length > 0 && (
          <div>
            <h4 className="font-semibold text-gray-900 text-sm mb-2">Improvement Actions</h4>
            <ul className="space-y-1.5">
              {r.suggestions.slice(0, 8).map((s, i) => (
                <li key={i} className="flex items-start gap-2 text-sm">
                  <span className={`text-xs font-semibold px-1.5 py-0.5 rounded flex-shrink-0 mt-0.5 ${priorityBadge(s.priority)}`}>{s.priority}</span>
                  <span className="text-gray-700">{s.text}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {/* Missing keywords */}
      {r?.keywordSuggestions && Object.keys(r.keywordSuggestions).length > 0 && (
        <div className="bg-white border border-gray-200 rounded-2xl p-6 mb-6 shadow-sm">
          <h3 className="font-bold text-gray-900 mb-1">Missing QA Keywords</h3>
          <p className="text-gray-500 text-xs mb-4">Add these to your Skills section to improve ATS keyword matching</p>
          <div className="space-y-3">
            {Object.entries(r.keywordSuggestions).slice(0, 5).map(([cat, keywords]) => (
              <div key={cat}>
                <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">{catLabels[cat] || cat}</div>
                <div className="flex flex-wrap gap-1.5">
                  {keywords.map(kw => <span key={kw} className="text-xs bg-blue-50 text-blue-700 border border-blue-200 px-2 py-0.5 rounded-full">{kw}</span>)}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Template picker */}
      <div className="bg-white border border-gray-200 rounded-2xl p-6 mb-6 shadow-sm">
        <h3 className="font-bold text-gray-900 mb-1">Choose Template</h3>
        <p className="text-gray-500 text-xs mb-4">All 6 templates are designed to be fully parsed by modern ATS systems</p>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {Object.entries(resumeTemplates).map(([key, tmpl]) => (
            <button
              key={key}
              onClick={() => setSelectedTemplate(key)}
              className={`p-4 rounded-2xl border-2 transition-all text-left active:scale-95 ${
                selectedTemplate === key
                  ? 'border-blue-600 bg-blue-50 shadow-md'
                  : 'border-gray-200 hover:border-gray-300 hover:shadow-sm'
              }`}
            >
              {/* Mini resume preview */}
              <div className="w-full h-24 rounded-xl mb-3 overflow-hidden bg-white border border-gray-100 p-2 flex flex-col gap-1">
                {/* Name bar */}
                <div className="h-2.5 rounded" style={{ backgroundColor: tmpl.colors.primary, width: '60%' }} />
                {/* Subtitle bar */}
                <div className="h-1.5 rounded bg-gray-200" style={{ width: '40%' }} />
                {/* Divider */}
                <div className="h-px my-0.5" style={{ backgroundColor: tmpl.colors.accent }} />
                {/* Section label */}
                <div className="h-1.5 rounded" style={{ backgroundColor: tmpl.colors.accent, width: '30%', opacity: 0.7 }} />
                {/* Content lines */}
                {[70, 85, 55, 75].map((w, i) => (
                  <div key={i} className="h-1 rounded bg-gray-200" style={{ width: `${w}%` }} />
                ))}
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <div className={`text-sm font-bold leading-tight ${
                    selectedTemplate === key ? 'text-blue-700' : 'text-gray-800'
                  }`}>{tmpl.name}</div>
                  <div className="text-xs text-gray-400 mt-0.5 leading-tight">{tmpl.description}</div>
                </div>
                {selectedTemplate === key && (
                  <div className="w-5 h-5 rounded-full bg-blue-600 flex items-center justify-center flex-shrink-0 ml-2">
                    <CheckCircle className="w-3 h-3 text-white" />
                  </div>
                )}
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Download actions */}
      <div className="flex flex-col sm:flex-row gap-3">
        <button onClick={handlePreview} className="flex-1 flex items-center justify-center gap-2 bg-gray-100 text-gray-700 px-5 py-3 rounded-xl hover:bg-gray-200 transition-colors font-medium text-sm min-h-[48px]">
          <Eye className="w-4 h-4" /> Preview
        </button>
        <button onClick={handlePrint} className="flex-1 flex items-center justify-center gap-2 bg-blue-50 text-blue-700 px-5 py-3 rounded-xl hover:bg-blue-100 transition-colors font-medium text-sm min-h-[48px]">
          <Download className="w-4 h-4" /> Print / Save as PDF
        </button>
        <button
          onClick={downloadResume}
          disabled={isDownloading}
          className="flex-1 flex items-center justify-center gap-2 bg-blue-600 text-white px-5 py-3 rounded-xl hover:bg-blue-700 transition-all font-semibold text-sm shadow-md disabled:opacity-60 disabled:cursor-not-allowed min-h-[48px]"
        >
          {isDownloading
            ? <><div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" /> Generating PDF...</>
            : <><Download className="w-4 h-4" /> Download PDF</>}
        </button>
      </div>

    </div>
  );
};

// ── Main component ────────────────────────────────────────────────────────────
const CompleteEnhancedResumeBuilder = ({
  onBack,
  userEmail,
  initialJobDescription = '',
  initialKeywordsToInject = [],
}) => {
  // STARTUP DIAGNOSTICS
  console.log('%c=== RESUME BUILDER STARTUP ===', 'color: blue; font-size: 16px; font-weight: bold');
  console.log('[Startup] STEPS configuration:', {
    count: STEPS.length,
    ids: STEPS.map(s => s.id),
    titles: STEPS.map(s => s.title)
  });
  console.log('[Startup] Props:', { onBack: !!onBack, userEmail });
  console.log('[Startup] localStorage:', {
    hasQADraft: !!localStorage.getItem('qa_resume_builder_v3'),
    allKeys: Object.keys(localStorage)
  });
  
  console.log('[CompleteEnhancedResumeBuilder] MOUNT', { onBack: !!onBack, userEmail });
  
  const form  = useResumeForm(userEmail);
  const steps = useResumeSteps(form.validateStep);
  const ai    = useResumeAI();
  
  console.log('[CompleteEnhancedResumeBuilder] RENDER', {
    currentStep: steps.currentStep,
    totalSteps: STEPS.length,
    currentStepId: STEPS[steps.currentStep]?.id,
    formDataKeys: Object.keys(form.formData || {}),
    hasErrors: Object.keys(form.errors || {}).length > 0
  });
  
  const [showMobileSidebar,       setShowMobileSidebar]       = useState(false);
  const [showBackConfirm,         setShowBackConfirm]         = useState(false);
  // Optional sections the user has opted into (shown inside CredentialsStep)
  const [enabledOptionalSections, setEnabledOptionalSections] = useState([]);
  const [isPremium,               setIsPremium]               = useState(false);
  const [showPremiumModal, setShowPremiumModal] = useState(false);
  const isKeyboardOpen = useKeyboardOpen();
  
  const [jobTargetOpen,           setJobTargetOpen]           = useState(false);
  const [showStartFreshConfirm,   setShowStartFreshConfirm]   = useState(false);
  const [isAiLimitReached,        setIsAiLimitReached]        = useState(() => typeof window !== 'undefined' && localStorage.getItem('ai_limit_reached') === 'true');
  
  const handleStartFreshConfirm = useCallback(() => {
    // 1. Wipe draft from local storage
    localStorage.removeItem('qa_resume_builder_v3');
    // 2. Reset form data to default
    form.resetForm();
    // 3. Jump to Step 1
    steps.goTo(0);
    // 4. Close modal and show success toast
    setShowStartFreshConfirm(false);
    toast.success('Draft cleared! Started a fresh resume.', { position: 'top-center' });
  }, [form, steps]);
  const { user, isVerified, sessionToken } = useAuth();

  const checkPremium = useCallback(async () => {
    const email = user?.email;
    if (!isVerified || !email || !supabase) {
      setIsPremium(false);
      return;
    }
    try {
      const { data, error } = await supabase
        .from('premium_users')
        .select('expires_at')
        .eq('user_email', email.toLowerCase().trim())
        .maybeSingle();
      if (!error && data?.expires_at) {
        setIsPremium(new Date(data.expires_at) > new Date());
      } else {
        setIsPremium(false);
      }
    } catch (e) {
      setIsPremium(false);
    }
  }, [user, isVerified]);

  useEffect(() => {
    checkPremium();
  }, [checkPremium]);

  // Listen to premium-activated event to update state in real-time
  useEffect(() => {
    const handleActivated = (e) => {
      const email = user?.email || '';
      if (!e.detail?.email || e.detail.email === email) {
        setIsPremium(true);
      }
    };
    window.addEventListener('premium-activated', handleActivated);
    return () => window.removeEventListener('premium-activated', handleActivated);
  }, [user]);

  // Catch limit-exceeded event to show error toast
  useEffect(() => {
    const handleLimitExceeded = () => {
      toast.dismiss(); // Clear any other toasts so this one stands out
      toast.error('Daily free AI limit reached! You will need to create the rest of your resume manually.', {
        duration: 5000,
        position: 'top-center'
      });
      setIsAiLimitReached(true);
      // Payment modal intentionally disabled for now
    };
    window.addEventListener('resume-ai-limit-exceeded', handleLimitExceeded);
    return () => window.removeEventListener('resume-ai-limit-exceeded', handleLimitExceeded);
  }, []);

  // ── Seed formData from pre-launch JD modal (auto-fill) ─────────────────────
  useEffect(() => {
    if (!initialJobDescription?.trim() && !initialKeywordsToInject?.length) return;

    // 1. Store the JD in formData
    if (initialJobDescription?.trim()) {
      form.setFormData(prev => ({ ...prev, jobDescription: initialJobDescription }));
    }

    // 2. Bulk-inject all approved keywords silently (no per-keyword toasts)
    const kws = initialKeywordsToInject || [];
    if (kws.length > 0) {
      kws.forEach(kw => form.injectKeyword(kw, { silent: true, isFromAI: true }));
    }

    // 3. Generate tailored summary (tries Gemini → falls back to local engine)
    if (initialJobDescription?.trim()) {
      setTimeout(async () => {
        const wasLimitHitBefore = localStorage.getItem('ai_limit_reached') === 'true';
        try {
          const summary = await ai.generateSummary({
            specialization: 'automation',
            yearsExp: 3,
            formData: form.formData,
          });
          if (summary) {
            form.setFormData(prev => ({ ...prev, summary }));
          }
        } catch {
          // generateSummary handles fallback internally — silent
        }
        
        const isLimitHitNow = localStorage.getItem('ai_limit_reached') === 'true';
        if (!wasLimitHitBefore && isLimitHitNow) {
          // Limit was hit during this specific call. The global listener just showed the red error toast.
          // We suppress the green success toast to avoid overlapping conflicting messages.
        } else {
          // Single success toast after everything is done
          toast.success(
            `✅ Resume tailored! ${kws.length} keywords added & summary updated.`,
            { duration: 4000, position: 'top-center' }
          );
        }
      }, 400);
    } else if (kws.length > 0) {
      // No JD text but keywords selected — still show confirmation
      toast.success(`✅ ${kws.length} keywords added to your resume!`, { duration: 3000, position: 'top-center' });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const contentRef = useRef(null);
  const lastScrollY = useRef(0);

  // Prevent layout shift on scroll
  useEffect(() => {
    const handleScroll = () => {
      if (contentRef.current) {
        const currentScrollY = window.scrollY;
        lastScrollY.current = currentScrollY;
      }
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleToggleOptionalSection = useCallback((sectionId) => {
    setEnabledOptionalSections(prev =>
      prev.includes(sectionId)
        ? prev.filter(id => id !== sectionId)
        : [...prev, sectionId]
    );
  }, []);

  const handleBack = useCallback(() => {
    if (!onBack) return;
    const hasProgress =
      form.formData.personal.name.trim() ||
      form.formData.personal.email.trim() ||
      form.formData.summary.trim() ||
      form.formData.experience.some(e => e.company.trim() || e.role.trim()) ||
      Object.values(form.formData.technicalSkills).flat().length > 0;
    if (hasProgress) setShowBackConfirm(true);
    else onBack();
  }, [onBack, form.formData]);

  const currentStepId = STEPS[steps.currentStep]?.id;
  
  // CRITICAL: Validate step bounds
  if (steps.currentStep < 0 || steps.currentStep >= STEPS.length) {
    console.error('[CompleteEnhancedResumeBuilder] INVALID STEP INDEX!', {
      currentStep: steps.currentStep,
      totalSteps: STEPS.length,
      allStepIds: STEPS.map(s => s.id)
    });
  }
  
  if (!currentStepId) {
    console.error('[CompleteEnhancedResumeBuilder] UNDEFINED STEP ID!', {
      currentStep: steps.currentStep,
      step: STEPS[steps.currentStep],
      allSteps: STEPS
    });
  }
  
  console.log('[CompleteEnhancedResumeBuilder] Current Step:', {
    index: steps.currentStep,
    id: currentStepId,
    title: STEPS[steps.currentStep]?.title,
    isFirst: steps.isFirst,
    isLast: steps.isLast,
    progress: steps.progress
  });

  // Build handlers object passed to step registry
  const handlers = {
    setFormData:       form.setFormData,
    toggleSkill:       form.toggleSkill,
    toggleTool:        form.toggleTool,
    toggleCompetency:  form.toggleCompetency,
    addItem:           form.addItem,
    removeItem:        form.removeItem,
    updateItem:        form.updateItem,
    addAchievement:    form.addAchievement,
    removeAchievement: form.removeAchievement,
    updateAchievement: form.updateAchievement,
    handleBlur:        form.handleBlur,
    handlePreview:     form.handlePreview,
    handlePrint:       form.handlePrint,
    downloadResume:    form.downloadResume,
    selectedTemplate:  form.selectedTemplate,
    setSelectedTemplate: form.setSelectedTemplate,
    atsResult:         form.atsResult,
    isDownloading:     form.isDownloading,
    injectKeyword:     form.injectKeyword,
  };
  
  // Validate handlers (only flag undefined functions/values, not boolean false)
  const missingHandlers = Object.entries(handlers).filter(([k, v]) => v === undefined).map(([k]) => k);
  if (missingHandlers.length > 0) {
    console.warn('[CompleteEnhancedResumeBuilder] MISSING HANDLERS:', missingHandlers);
  }

  // AI handlers object — passed to every step that has AI features
  const aiHandlers = {
    generateSummary:               ai.generateSummary,
    generateSummaryWithVariations: ai.generateSummaryWithVariations,
    enhanceBullet:         ai.enhanceBullet,
    generateSTARBullet:    ai.generateSTARBullet,
    suggestSkillsForRole:  ai.suggestSkillsForRole,
    analyzeJobDescription: ai.analyzeJobDescription,
    analyzeJobDescriptionWithProfile: ai.analyzeJobDescriptionWithProfile,
    getAtsKeywordSuggestions: ai.getAtsKeywordSuggestions,
  };

  // Render current step — registry handles lazy loading, review is inline
  console.log('[CompleteEnhancedResumeBuilder] Rendering step:', currentStepId);
  
  let stepContent;
  try {
    if (currentStepId === 'review') {
      console.log('[CompleteEnhancedResumeBuilder] Rendering ReviewStep');
      stepContent = <ReviewStep
        atsResult={form.atsResult}
        selectedTemplate={form.selectedTemplate}
        setSelectedTemplate={form.setSelectedTemplate}
        handlePreview={form.handlePreview}
        handlePrint={form.handlePrint}
        downloadResume={form.downloadResume}
        isDownloading={form.isDownloading}
      />;
    } else if (currentStepId) {
      console.log('[CompleteEnhancedResumeBuilder] Rendering step from registry:', currentStepId);
      stepContent = renderStep(currentStepId, {
        formData:                  form.formData,
        setFormData:               form.setFormData,
        errors:                    form.errors,
        touched:                   form.touched,
        warnings:                  form.warnings,
        fieldLengths:              form.fieldLengths,
        maxLengths:                form.maxLengths,
        handlers,
        enabledOptionalSections,
        onToggleOptionalSection:   handleToggleOptionalSection,
        aiHandlers,
        isPremium,
        isAiLimitReached,
      });
    } else {
      console.error('[CompleteEnhancedResumeBuilder] No step ID - showing fallback');
      stepContent = (
        <div className="flex items-center justify-center py-16">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-2 border-blue-600 border-t-transparent mx-auto mb-3" />
            <p className="text-gray-500 text-sm">Loading step...</p>
          </div>
        </div>
      );
    }
  } catch (error) {
    console.error('[CompleteEnhancedResumeBuilder] ERROR rendering step:', error);
    stepContent = (
      <div className="flex items-center justify-center py-16">
        <div className="text-center">
          <p className="text-red-500 text-sm mb-2">Failed to load step</p>
          <button 
            onClick={() => window.location.reload()} 
            className="px-4 py-2 bg-blue-600 text-white rounded-lg"
          >
            Reload Page
          </button>
        </div>
      </div>
    );
  }


  return (
    <div ref={contentRef} className="min-h-[100dvh] bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 pb-safe relative" data-builder-version="6-step-v1">

      {/* Floating AI Limit Pill */}
      {isAiLimitReached && (
        <div className="fixed top-[80px] left-1/2 -translate-x-1/2 z-[9999] animate-fadeIn pointer-events-none w-[90%] max-w-fit">
          <div className="bg-white/85 backdrop-blur-md border border-orange-200/80 shadow-[0_8px_30px_rgb(249,115,22,0.15)] px-4 py-2 rounded-full flex items-center justify-center gap-2.5 pointer-events-auto mx-auto">
            <div className="w-6 h-6 rounded-full bg-orange-100 flex items-center justify-center flex-shrink-0 shadow-inner">
              <AlertCircle className="w-3.5 h-3.5 text-orange-600" />
            </div>
            <p className="text-xs sm:text-sm font-bold text-gray-800 pr-1 truncate">
              Daily AI limit reached. <span className="font-medium text-gray-500 hidden sm:inline">Free usage resets in 24 hours.</span>
            </p>
          </div>
        </div>
      )}

      {/* ── Job Target Flow (opens once per session or on Change Job) ── */}
      <JobTargetFlow
        isOpen={jobTargetOpen}
        onClose={() => {
          sessionStorage.setItem(JT_DISMISSED_KEY, '1');
          setJobTargetOpen(false);
        }}
        onComplete={(jdText, approved, analysis, startFresh) => {
          form.setFormData(prev => ({ ...prev, jobDescription: jdText }));
          
          if (approved?.length > 0) {
            approved.forEach(kw => form.injectKeyword(kw, { silent: true, isFromAI: true }));
          }

          setTimeout(async () => {
            const wasLimitHitBefore = localStorage.getItem('ai_limit_reached') === 'true';
            try {
              const summary = await ai.generateSummary({
                specialization: 'automation',
                yearsExp: 3,
                formData: form.formData,
              });
              if (summary) {
                form.setFormData(prev => ({ ...prev, summary }));
              }
            } catch (err) {
              // fallback is handled internally
            }
            const isLimitHitNow = localStorage.getItem('ai_limit_reached') === 'true';
            if (!(wasLimitHitBefore === false && isLimitHitNow === true)) {
              toast.success(`✅ ${approved.length} keywords added & summary updated!`, {
                duration: 4000,
                position: 'top-center'
              });
            }
          }, 400);

          sessionStorage.setItem(JT_DISMISSED_KEY, '1');
          setJobTargetOpen(false);
        }}
        analyzeJobDescriptionAsync={ai.analyzeJobDescription}
        mode="mid-session"
        initialJdText={form.formData?.jobDescription || ''}
      />

      {/* ── Sticky progress header ── */}
      <div className={`bg-white border-b sticky top-0 z-20 shadow-sm transition-all duration-300 ${isKeyboardOpen ? 'py-1' : ''}`}>
        {/* Re-target banner for returning users with saved JD */}
        <div className={`overflow-hidden transition-all duration-300 ${isKeyboardOpen ? 'h-0 opacity-0 lg:h-auto lg:opacity-100' : 'h-auto opacity-100'}`}>
          <JobTargetBanner
            formData={form.formData}
            onOpen={() => setJobTargetOpen(true)}
          />
        </div>
        <div className={`px-3 sm:px-6 transition-all duration-300 ${isKeyboardOpen ? 'py-1' : 'py-2.5 sm:py-3'}`}>
        <div className={`flex items-center justify-between ${isKeyboardOpen ? 'mb-0 lg:mb-2.5' : 'mb-2.5'}`}>
          <div className="flex items-center gap-2">
            {onBack && (
              <>
                <button
                  onClick={handleBack}
                  aria-label="Back to builder selection"
                  className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-700 bg-gray-50 border border-gray-200 rounded-lg hover:bg-gray-100 active:scale-95 transition-all min-h-[44px]"
                >
                  <ArrowLeft className="w-4 h-4 flex-shrink-0" />
                  <span>Back</span>
                </button>
                <span className="w-px h-5 bg-gray-200" aria-hidden="true" />
              </>
            )}
            {/* Mobile step indicator — opens sidebar */}
            <button
              className="lg:hidden flex items-center gap-1.5 text-xs sm:text-sm font-medium text-gray-700 min-h-[44px] px-1.5 sm:px-2 active:scale-95 transition-transform"
              onClick={() => setShowMobileSidebar(true)}
              aria-label="Open section navigator"
            >
              <span className="w-6 h-6 sm:w-7 sm:h-7 bg-blue-600 text-white rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0">
                {steps.currentStep + 1}
              </span>
              <span className="max-w-[100px] sm:max-w-[140px] truncate">{STEPS[steps.currentStep]?.title}</span>
              <span className="text-gray-400 text-xs">▾</span>
            </button>
            {/* Desktop: phase label + step dots */}
            <div className="hidden lg:flex items-center gap-3">
              <span className="text-xs font-bold text-gray-400 uppercase tracking-widest">
                {steps.phaseLabel || 'Loading'}
              </span>
              <div className="flex items-center gap-1.5">
                {(steps.phaseSteps || []).map((_, i) => (
                  <div
                    key={i}
                    className={`rounded-full transition-all duration-300 ${
                      i < steps.phaseIndex     ? 'w-2 h-2 bg-blue-600'
                      : i === steps.phaseIndex ? 'w-3 h-3 bg-blue-600 ring-2 ring-blue-200'
                      : 'w-2 h-2 bg-gray-300'
                    }`}
                  />
                ))}
              </div>
              <span className="text-xs text-gray-400">
                Step {steps.currentStep + 1} of {STEPS.length}
              </span>
            </div>
          </div>
          <div className="flex items-center gap-2 sm:gap-3">
            <button
              onClick={() => setShowStartFreshConfirm(true)}
              className="hidden sm:inline-flex items-center gap-1.5 text-xs font-semibold text-red-500 bg-red-50 hover:bg-red-100 hover:text-red-700 px-2.5 py-1.5 rounded-lg transition-colors active:scale-95"
              title="Erase all data and start over"
            >
              <Trash2 className="w-3.5 h-3.5" />
              <span>Start Fresh</span>
            </button>
            {form.lastSavedAt && (
              <span className="text-xs text-gray-400 hidden md:inline tabular-nums">
                Saved {form.lastSavedAt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
              </span>
            )}
            {/* Live ATS score pill — animated, level-aware */}
            <div className={`hidden sm:block transition-all duration-300 ${isKeyboardOpen ? 'opacity-0 scale-95 lg:opacity-100 lg:scale-100' : 'opacity-100 scale-100'}`}>
              <ATSScoreHeaderPill atsResult={form.atsResult} />
            </div>
            <span className={`text-xs sm:text-sm font-semibold text-gray-600 tabular-nums transition-all duration-300 ${isKeyboardOpen ? 'lg:block hidden' : 'block'}`}>{steps.progress}%</span>
          </div>
        </div>
        {/* Segmented progress bar — one segment per step */}
        <div className={`flex gap-1 overflow-hidden transition-all duration-300 ${isKeyboardOpen ? 'h-0 opacity-0 lg:h-1.5 lg:opacity-100 mt-0 lg:mt-1' : 'h-1.5 opacity-100 mt-1'}`}>
          {(STEPS || []).map((_, i) => (
            <div
              key={i}
              className={`h-1.5 flex-1 rounded-full transition-all duration-300 ${
                i < steps.currentStep  ? 'bg-blue-600'
                : i === steps.currentStep ? 'bg-blue-400'
                : 'bg-gray-200'
              }`}
            />
          ))}
        </div>
        </div>
      </div>

      {/* ── Mobile sidebar overlay ── */}
      {showMobileSidebar && (
        <div className="lg:hidden fixed inset-0 z-50 flex animate-fadeIn">
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setShowMobileSidebar(false)} />
          <div className="relative bg-white w-80 max-w-[85vw] h-full overflow-y-auto shadow-2xl animate-slideInLeft">
            <div className="flex items-center justify-between p-4 border-b sticky top-0 bg-white z-10">
              <h3 className="font-bold text-gray-900">Resume Sections</h3>
              <button onClick={() => setShowMobileSidebar(false)} className="p-2 rounded-lg hover:bg-gray-100 active:scale-95 transition-all min-h-[44px] min-w-[44px] flex items-center justify-center">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-3 space-y-1.5 pb-safe">
              {(STEPS || []).map((step, index) => (
                <SidebarItem
                  key={step?.id || index}
                  step={step}
                  index={index}
                  isActive={steps.currentStep === index}
                  isCompleted={steps.currentStep > index}
                  onClick={i => { steps.goTo(i); setShowMobileSidebar(false); }}
                />
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ── Main layout ── */}
      <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-3 sm:py-4 lg:py-8 pb-24 sm:pb-8">
        <div className="grid lg:grid-cols-4 gap-4 lg:gap-8">

          {/* Desktop sidebar */}
          <div className="hidden lg:block lg:col-span-1">
            <div className="sticky top-20 space-y-4" style={{ maxHeight: 'calc(100dvh - 88px)', overflowY: 'auto' }}>
              {/* ATS Score Widget */}
              <div className="bg-white rounded-2xl shadow-xl p-4">

                <ATSScoreSidebar
                  atsResult={form.atsResult}
                  formData={form.formData}
                  injectKeyword={form.injectKeyword}
                  isReviewStep={currentStepId === 'review'}
                  onNavigateToSkills={() => {
                    const skillsIdx = STEPS.findIndex(s => s.id === 'skills-expertise');
                    if (skillsIdx !== -1) steps.goTo(skillsIdx);
                  }}
                />
              </div>

              {/* Step navigator */}
              <div className="bg-white rounded-2xl shadow-xl p-4">
                <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-3 px-1">Sections</h3>
                <div className="space-y-1">
                  {(STEPS || []).map((step, index) => (
                    <SidebarItem
                      key={step?.id || index}
                      step={step}
                      index={index}
                      isActive={steps.currentStep === index}
                      isCompleted={steps.currentStep > index}
                      onClick={steps.goTo}
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Step content */}
          <div className="lg:col-span-3">
            <div className="bg-white rounded-xl sm:rounded-2xl shadow-xl p-3 sm:p-6 lg:p-8">
              <div className="lg:overflow-y-auto lg:max-h-[calc(100dvh-300px)] lg:pr-4">
                {stepContent}
              </div>

              {/* Desktop bottom nav */}
              <div className="hidden sm:flex justify-between mt-6 pt-4 sm:pt-6 border-t border-gray-200">
                <button
                  onClick={steps.goPrev}
                  disabled={steps.isFirst}
                  className="flex items-center gap-2 px-4 sm:px-6 py-3 border-2 border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 disabled:opacity-50 transition-all min-h-[48px]"
                >
                  <ArrowLeft className="w-4 h-4" />
                  <span>Previous</span>
                </button>
                {!steps.isLast && (
                  <button
                    onClick={steps.goNext}
                    className="flex items-center gap-2 bg-gradient-to-r from-blue-600 to-blue-700 text-white px-4 sm:px-6 py-3 rounded-xl font-semibold hover:from-blue-700 hover:to-blue-800 transition-all min-h-[48px]"
                  >
                    Next Step
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── Mobile sticky bottom navigation ── */}
      {!isKeyboardOpen && (
        <div className="sm:hidden fixed bottom-0 left-0 right-0 z-30 bg-white border-t border-gray-200 shadow-2xl pb-safe">
          <div className="flex items-center justify-between px-3 py-2.5">
            <button
              onClick={steps.goPrev}
              disabled={steps.isFirst}
              className="flex items-center gap-1.5 px-4 py-2.5 border-2 border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed active:scale-95 transition-all min-h-[48px] font-semibold text-sm"
            >
              <ArrowLeft className="w-4 h-4" />
              Prev
            </button>
            
            {/* Mobile progress indicator */}
            <div className="flex-1 mx-3 text-center">
              <div className="text-xs font-bold text-gray-900">
                Step {steps.currentStep + 1} of {STEPS.length}
              </div>
              <div className="flex gap-1 mt-1.5 justify-center">
                {(STEPS || []).map((_, i) => (
                  <div
                    key={i}
                    className={`h-1 rounded-full transition-all ${
                      i < steps.currentStep  ? 'w-3 bg-blue-600'
                      : i === steps.currentStep ? 'w-6 bg-blue-600'
                      : 'w-3 bg-gray-200'
                    }`}
                  />
                ))}
              </div>
            </div>

            {!steps.isLast ? (
              <button
                onClick={steps.goNext}
                className="flex items-center gap-1.5 bg-gradient-to-r from-blue-600 to-blue-700 text-white px-4 py-2.5 rounded-xl font-bold hover:from-blue-700 hover:to-blue-800 active:scale-95 transition-all min-h-[48px] shadow-lg text-sm"
              >
                Next
                <ChevronRight className="w-4 h-4" />
              </button>
            ) : (
              <button
                onClick={form.downloadResume}
                disabled={form.isDownloading}
                className="flex items-center gap-1.5 bg-gradient-to-r from-green-600 to-green-700 text-white px-4 py-2.5 rounded-xl font-bold hover:from-green-700 hover:to-green-800 active:scale-95 transition-all min-h-[48px] shadow-lg text-sm disabled:opacity-60"
              >
                <Download className="w-4 h-4" />
                Done
              </button>
            )}
          </div>
        </div>
      )}

      {/* ── Mobile ATS drawer ── */}
      <ATSScoreMobileDrawer
        atsResult={form.atsResult}
        formData={form.formData}
        injectKeyword={form.injectKeyword}
        onNavigateToSkills={() => {
          const skillsIdx = STEPS.findIndex(s => s.id === 'skills-expertise');
          if (skillsIdx !== -1) steps.goTo(skillsIdx);
        }}
      />

      {/* ── Back confirmation modal ── */}
      {showBackConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl max-w-sm w-full p-6">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
                <ArrowLeft className="w-5 h-5 text-amber-600" />
              </div>
              <h3 className="text-lg font-bold text-gray-900">Leave the builder?</h3>
            </div>
            <p className="text-gray-600 text-sm mb-6">Your progress is auto-saved. You can return and continue where you left off.</p>
            <div className="flex gap-3">
              <button onClick={() => setShowBackConfirm(false)} className="flex-1 px-4 py-2.5 border-2 border-gray-200 text-gray-700 rounded-xl font-semibold hover:bg-gray-50 transition-all min-h-[44px]">
                Stay
              </button>
              <button onClick={() => { setShowBackConfirm(false); onBack(); }} className="flex-1 px-4 py-2.5 bg-blue-600 text-white rounded-xl font-semibold hover:bg-blue-700 transition-all min-h-[44px]">
                Leave
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Start Fresh Confirmation Modal */}
      {showStartFreshConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fadeIn">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden animate-scaleIn">
            <div className="p-6">
              <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center mb-4">
                <Trash2 className="w-6 h-6 text-red-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Start a Fresh Resume?</h3>
              <p className="text-sm text-gray-500 mb-6 leading-relaxed">
                This will permanently delete all the progress in your current draft. You will start over from a completely blank canvas. This action cannot be undone.
              </p>
              <div className="flex flex-col sm:flex-row gap-3">
                <button
                  type="button"
                  onClick={() => setShowStartFreshConfirm(false)}
                  className="flex-1 px-4 py-3 text-sm font-semibold text-gray-700 bg-gray-100 rounded-xl hover:bg-gray-200 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleStartFreshConfirm}
                  className="flex-1 px-4 py-3 text-sm font-semibold text-white bg-red-600 rounded-xl hover:bg-red-700 active:scale-95 transition-all shadow-sm flex items-center justify-center gap-2"
                >
                  <Trash2 className="w-4 h-4" />
                  Erase Draft
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <PremiumModal
        isOpen={showPremiumModal}
        onClose={() => setShowPremiumModal(false)}
        userEmail={user?.email || ''}
        sessionToken={sessionToken}
      />
    </div>
  );
};

export default CompleteEnhancedResumeBuilder;
